import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Row, Col, Form, Button, Dropdown, Card, Modal } from "react-bootstrap";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  TICKETING,
  CREATENEW,
  FILTER_TYPES,
} from "../../../../shared/constants/Constants";
import Breadcrumbs from "../../../../shared/components/Breadcrumbs";
import DefaultButton from "../../../../shared/components/DefaultButton";
import {
  loadUserTicketFilterAddDataRequest,
  loadUserTicketFilterGetDataRequest,
  loadUserTicketFilterDeleteDataRequest,
  loadAdminTicketsQueryRequest,
  loadAdminTicketsRowCountRequest,
  loadTicketFilterSaveDataRequest,
  loadTicketBulkUpdateRequest,
} from "../redux/Actions";
import { loadStatusDropdownRequest } from "../../../vision_hub/escalation_rule_builder/redux/Actions";
import { loadCategorySubCategoryConfigDataRequest } from "../../../vision_hub/categories/redux/Actions";
import ServerSideGrid from "../../../../shared/components/listview/ServerSideGrid";
import CustomSelectFilter from "../../../../shared/components/listview/CustomSelectFilter";
import DependentCustomSelectFilter from "../../../../shared/components/listview/DependentCustomSelectFilter";
import { addTableFilter } from "../../../table_filters_redux/Actions";
import TablePaging from "../../../../shared/components/TablePaging";
import dropdownFilterFunction from "../../../../shared/components/listview/dropdownFilterFunction";
import "./Ticketing.css";
import { GROUPBYTYPE_ADMIN_GET_DATA} from "../../../group_jobtitles/redux/Constants";
import { loadGroupByIDJobTitlesGetDataRequest, loadGroupByTypeAdminGetDataRequest } from "../../../group_jobtitles/redux/Actions";
import { setNextPrevIds } from "../../../next_prev_redux/Actions";
import { instance } from "../../../../shared/utilites/ApiClient";
import { loadAdminGroupGetDataRequest } from "../../../vision_hub/admin_group/redux/Actions";
import { loadCompanyDataRequest } from "../../../company/redux/Actions";

const Ticketing = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [query] = useSearchParams();

  // Redux selectors
  const { queryData, rowCount, loadingTicket: loadingAdminTickets } = useSelector(
    (store) => store.adminTickets
  );
  const { companies } = useSelector((state) => state.company);
  const { userFilterList = [] } = useSelector((state) => state.userTicketFilter);
  const { statusList = [] } = useSelector((state) => state.ticketingDropdowns || {});
  const { adminGroup = [], group= [] } = useSelector((state) => state.groupJobtitle);
  const rawCategoryConfig = useSelector((state) => state.categories?.categoryconfig);
  const { tableFilters } = useSelector((state) => state.tableFilter);
  const { adminGroupAll = [] } = useSelector((state) => state.AdminGroup);
  const { bulkUpdateSuccess } = useSelector((store) => store.ticketing);
  // In your list page, when data loads:
    useEffect(() => {
    if (queryData?.length) {
        const idObjects = queryData.map(item => ({
            id: item.id,
            type: item.ticket_type
        }));
        dispatch(setNextPrevIds(idObjects, TICKETING ));
    }
}, [queryData]);

  useEffect(() => {
    dispatch(loadAdminGroupGetDataRequest());
  },[dispatch]);

  // Constants
  const pageSize = 80;
  const userRole = "admin";
  const emp_no = JSON.parse(localStorage.getItem("emp_no"));
  const exportTitle = TICKETING;
  const tableFilterKey = useMemo( () => location.pathname + "/" + exportTitle, [location]  );
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Local state
const savedState = tableFilters[tableFilterKey];
console.log('🔍 Saved state from Redux:', savedState);

const [selectedFilterId, setSelectedFilterId] = useState(savedState?._selectedFilterId ?? null);
const [filterLabel, setFilterLabel] = useState(savedState?._filterLabel ?? "My Group Tickets");
const [externalGridFilter, setExternalGridFilter] = useState(
  savedState
    ? {
        filterModel: JSON.parse(savedState.filter || '{}'),
        sortModel: JSON.parse(savedState.sort || 'null'),
        pageSize: savedState.pageSize,
        columnVisibility: savedState.columnVisibility || null,
        columnOrder: savedState.columnOrder || [],
      }
    : null
);
const [filterAndPage, setFilterAndPage] = useState(
  savedState
    ? { filter: savedState.filter, page: savedState.page, sort: savedState.sort,
        pageSize: savedState.pageSize, defaultFilter: savedState.defaultFilter }
    : { filter: JSON.stringify({}), page: 1, sort: "null", pageSize: pageSize, defaultFilter: null }
);
const [bulkUpdate, setBulkUpdate] = useState({
  status_id: "",
  assigned_to: "",
  assigned_to_admin_group: "",
});
const [bulkMessage, setBulkMessage] = useState({ text: "", type: "" }); 
const [ticketRowCount, setTicketRowCount] = useState(0);
const [isReady, setIsReady] = useState(false);


  // Refs
  const isInitialized = useRef(false);
  const isFilterChanging = useRef(false);
  const isGridStabilized = useRef(false);
  const pendingFilterNameRef = useRef(null); 


  // Process category config
  const categoryconfig = Array.isArray(rawCategoryConfig)
    ? rawCategoryConfig
    : Array.isArray(rawCategoryConfig?.data)
    ? rawCategoryConfig.data
    : [];

  const categoryDataReady = categoryconfig.length > 0;
  

  // Dropdown options
  const statusOptions = useMemo(() => {
      const baseStatuses = statusList.map((entry) => ({
        value: entry.label,
        label: entry.label,
      }));
      return [
        { value: "ACTIVE", label: "Active" },
        ...baseStatuses,
      ];
    }, [statusList]); 
  const statusFunc = dropdownFilterFunction(statusOptions);

  const categoryOptions = useMemo(() => {
    const names = [...new Set(categoryconfig.map((c) => c.name))];
    return names.map((name) => ({ value: name, label: name }));
  }, [categoryconfig]);
  const categoryFunc = dropdownFilterFunction(categoryOptions);

  const subCategoryOptions = useMemo(() => {
    const allSubs = categoryconfig
      .flatMap((c) => c.sub_category || [])
      .map((sub) => sub.s_name);
    const uniqueSubs = [...new Set(allSubs)];
    return uniqueSubs.map((name) => ({ value: name, label: name }));
  }, [categoryconfig]);
  const subcategoryFunc = dropdownFilterFunction(subCategoryOptions);

  // Process adminGroupAll data from API
  const adminGroupAllData = useMemo(() => {
    // Handle both response formats: { data: [...] } or [...]
    return Array.isArray(adminGroupAll)
      ? adminGroupAll
      : Array.isArray(adminGroupAll?.data)
        ? adminGroupAll.data
        : [];
  }, [adminGroupAll]);

  // Admin Group dropdown options
  const adminGroupOptions = useMemo(() => {
    const options = adminGroupAllData.map((entry) => ({
      value: entry.group_name,
      label: entry.group_name
    }));
    // Add "Unassigned" option at the top
    return [
      { value: 'Unassigned', label: 'Unassigned' },
      ...options
    ];
  }, [adminGroupAllData]);
  const adminGroupFunc = dropdownFilterFunction(adminGroupOptions);

  //company option
  const companyOptions = useMemo(() => {
    if (!companies) return [];
    return companies.map((c) => ({ value: c.company_name, label: c.company_name }));
  }, [companies]);

  // Create a map of admin group name -> employees
  const adminGroupEmployeesMap = useMemo(() => {
    const map = {};

    // Add "Unassigned" as a virtual group
    map['Unassigned'] = ['Unassigned'];

    // Add all other admin groups
    adminGroupAllData.forEach(group => {
      if (group.employees && Array.isArray(group.employees)) {
        map[group.group_name] = group.employees.map(emp =>
          emp.employee_name?.trim() || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
        ).filter(Boolean);
      }
    });
    return map;
  }, [adminGroupAllData]);

  // Get all unique admin users (for when no group is selected)
  const allAdminUsers = useMemo(() => {
    const allUsers = adminGroupAllData.flatMap(group =>
      (group.employees || []).map(emp =>
        emp.employee_name?.trim() || `${emp.first_name || ''} ${emp.last_name || ''}`.trim()
      )
    );
    // Add "Unassigned" to the list
    return ['Unassigned', ...new Set(allUsers)].filter(Boolean).sort();
  }, [adminGroupAllData]);

  // Memoize the getOptions function to prevent unnecessary re-renders
  const getAssignedToOptions = useCallback((selectedAdminGroups) => {
      if (!selectedAdminGroups || selectedAdminGroups.length === 0) {
        return ["Unassigned", ...allAdminUsers];  // ← add Unassigned at top
      }

      const users = [];
      selectedAdminGroups.forEach(groupName => {
        if (adminGroupEmployeesMap[groupName]) {
          users.push(...adminGroupEmployeesMap[groupName]);
        }
      });

      return ["Unassigned", ...new Set(users)].sort((a, b) => {
        if (a === "Unassigned") return -1;  // keep Unassigned at top after sort
        if (b === "Unassigned") return 1;
        return a.localeCompare(b);
      });
    }, [allAdminUsers, adminGroupEmployeesMap]);

  // Memoize API payload to prevent recreating on every render
  const serverSideApiPayload = useMemo(() => ({
    defaultFilter: filterAndPage.defaultFilter,
  }), [userRole, emp_no, filterAndPage.defaultFilter]);

  // updating cate/sub cate 
    useEffect(() => {
      if (!gridRef.current || categoryOptions.length === 0) return;
      const api = gridRef.current.getApi();
      if (!api) return;

      // 1. Save current filter model before destroying
      const currentFilterModel = api.getFilterModel();

      // 2. Destroy stale filter instances
      api.destroyFilter('category');
      api.destroyFilter('sub_category');

      // 3. Reapply the saved filter model after destroy
      if (Object.keys(currentFilterModel).length > 0) {
        setTimeout(() => {
          api.setFilterModel(currentFilterModel);
        }, 0);
      }
    }, [categoryOptions, subCategoryOptions]);

  // Simple AG Grid column definitions for server-side model
  const columnDefs = useMemo(() => {
  const cols = [
    {
      headerName: "Top 50",
      field: "priority",
      type: "priority",   
      width: 120,
      pinned: "left"
    },
    {
      headerName: "Ticket ID",
      field: "ticket_number",
      filter: "agTextColumnFilter",
      sortable: true,
      width: 150,
      pinned: "left",
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'endsWith', 'inArray'],
        defaultOption: 'contains',
        suppressAndOrCondition: true,
        trimInput: true,
      },
    },
    {
      headerName: "Ticket Type",
      field: "ticket_type",
      filter: CustomSelectFilter,
      sortable: true,
      width: 120,
      filterParams: {
        options: ["Ticket", "Workflow"],
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Category",
      field: "category",
      filter: CustomSelectFilter,
      sortable: true,
      width: 150,
      filterParams: {
        options: categoryOptions.length > 0 ? categoryOptions.map(o => o.label) : [],
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Sub Category",
      field: "sub_category",
      filter: DependentCustomSelectFilter,
      sortable: true,
      width: 150,
      filterParams: {
        dependsOn: 'category',
        getOptions: (selectedCategories, allData) => {
          // If no category selected, return all subcategories
          if (!selectedCategories || selectedCategories.length === 0) {
            return subCategoryOptions.map(o => o.label);
          }

          // Filter subcategories based on selected categories
          const filteredSubCategories = categoryconfig
            .filter(config => selectedCategories.includes(config.name))
            .flatMap(config => (config.sub_category || []).map(sub => sub.s_name))
            .filter((value, index, self) => self.indexOf(value) === index); // unique values

          return filteredSubCategories;
        },
        allData: categoryconfig,
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Summary",
      field: "title",
      filter: "agTextColumnFilter",
      flex: 1,
      minWidth: 200,
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'endsWith', 'inArray'],
        defaultOption: 'contains',
        suppressAndOrCondition: true,
        trimInput: true,
      },
      // wrapText, autoHeight, cellStyle now applied from defaultColDef
    },
    {
      headerName: "Status",
      field: "status_name",
      filter: CustomSelectFilter,
      sortable: true,
      width: 120,
      filterParams: {
        options: statusOptions.map(o => o.label),
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Admin Group",
      field: "assigned_admin_group_name",
      filter: CustomSelectFilter,
      sortable: true,
      width: 160,
      filterParams: {
        options: adminGroupOptions.map(o => o.label),
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Assigned To",
      field: "assigned_to_name",
      filter: DependentCustomSelectFilter,
      sortable: true,
      width: 160,
      filterParams: {
        dependsOn: 'assigned_admin_group_name',
        getOptions: getAssignedToOptions,
        sortOptions: false, 
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Requested User",
      field: "requested_by_name",
      filter: "agTextColumnFilter",
      sortable: true,
      width: 150,
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'endsWith', 'inArray'],
        defaultOption: 'contains',
        suppressAndOrCondition: true,
        trimInput: true,
      },
    },
    {
      headerName: "Company",
      field: "company_name",
      filter: CustomSelectFilter,
      sortable: true,
      width: 130,
      filterParams: {
        options: companyOptions.map(o => o.label),
        allowBlank: true,
        buttons: ['apply', 'reset'],
        closeOnApply: true,
      },
    },
    {
      headerName: "Location",
      field: "location",
      filter: "agTextColumnFilter",
      sortable: true,
      width: 180,
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'endsWith', 'inArray'],
        defaultOption: 'contains',
        suppressAndOrCondition: true,
        trimInput: true,
      },
    },
    {
      headerName: "Requested On",
      field: "created_on",
      filter: "agDateColumnFilter",
      sortable: true,
      width: 150,
      filterParams: {        
        defaultOption: 'equals',
        suppressAndOrCondition: true,
        trimInput: true,
      },
    },
    {
      headerName: "Updated On",
      field: "updated_on",
      filter: "agDateColumnFilter",
      sortable: true,
      width: 150,
      filterParams: {        
        defaultOption: 'equals',
        suppressAndOrCondition: true,
        trimInput: true,
      },
    },  
    ];
    return isMobile ? cols.map(col => ({ ...col, pinned: undefined })) : cols;
  }, [categoryOptions, subCategoryOptions, statusOptions, adminGroupOptions, categoryconfig, getAssignedToOptions, companyOptions, isMobile]);

  // Initial data load
  useEffect(() => {
    dispatch(loadCategorySubCategoryConfigDataRequest());
    dispatch(loadStatusDropdownRequest());
    dispatch(loadUserTicketFilterGetDataRequest(FILTER_TYPES.TICKETS_LIST));
    // Admin groups with employees already loaded via useEffect 
    dispatch(loadGroupByTypeAdminGetDataRequest('admin'));
    dispatch(loadCompanyDataRequest()); 
  }, [dispatch]);

  // Initialize with last_user_filter when userFilterList loads
    useEffect(() => {
    if (isInitialized.current || !userFilterList || userFilterList.length === 0) {
      return;
    }

    // If we already restored state from Redux tableFilters, just mark as initialized
    if (tableFilters[tableFilterKey]) {
      console.log("Already restored saved filter state from Redux");
      isInitialized.current = true;
      setIsReady(true);
      return;
    }
    console.log("Initializing with user filters:", userFilterList);

    // Find the filter to apply (priority: last_user_filter > my_group_tickets > first)
    const filterToApply =
      userFilterList.find((f) => f.last_user_filter === true) ||
      userFilterList.find((f) => f?.filter_criteria?.filter_name === "my_group_tickets") ||
      userFilterList[0];

    if (filterToApply) {
      console.log("Applying initial filter:", filterToApply.filter_name);
      setSelectedFilterId(filterToApply.id);
      setFilterLabel(filterToApply.filter_name);

      const criteria = {
        ...(filterToApply.filter_criteria || {}),
        pageSize: filterToApply.page_size || pageSize,
      };

      setExternalGridFilter(criteria);
      isInitialized.current = true;
      setIsReady(true);
    }
  }, [userFilterList]);

  // Handle filter callback from grid (when user filters in the grid)
  const filterCallback = useCallback((newFilters) => {
    if (!isInitialized.current || isFilterChanging.current) {
      return;
    }

    console.log("Grid filter changed:", newFilters);

    const newFilterAndPage = {
      filter: JSON.stringify(newFilters.filter || {}),
      page: 1,
      sort: JSON.stringify(newFilters.sort || null),
      pageSize: newFilters.pageSize || pageSize,
      defaultFilter: newFilters.defaultFilter || null,
    };

    setFilterAndPage(newFilterAndPage);
  }, []);

  // Load data when filterAndPage changes
  useEffect(() => {
    if (!isInitialized.current) {
      return;
    }
    // If a filter save is pending, don't persist label/id yet —
      if (!pendingFilterNameRef.current) {
        dispatch(addTableFilter(tableFilterKey, {
          ...filterAndPage,
          _filterLabel: filterLabel,
          _selectedFilterId: selectedFilterId,
        }));
      }

    console.log("Loading tickets with filter:", filterAndPage);

    dispatch(addTableFilter(tableFilterKey, { ...filterAndPage, _filterLabel: filterLabel, _selectedFilterId: selectedFilterId, }));
    
    const filter = JSON.parse(filterAndPage.filter);
    const sort = JSON.parse(filterAndPage.sort);

    dispatch(
      loadAdminTicketsRowCountRequest({
        filter,
        defaultFilter: filterAndPage.defaultFilter,
      })
    );

    dispatch(
      loadAdminTicketsQueryRequest({
        filter,
        page: filterAndPage.page,
        sort,
        pageSize: filterAndPage.pageSize,
        defaultFilter: filterAndPage.defaultFilter,
      })
    );
  }, [filterAndPage, dispatch, tableFilterKey]);

  // Handle dropdown filter selection
  const handleFilterSelection = (selectedFilter) => {
    console.log("=== FILTER SELECTION START ===");
    console.log("Selected filter:", selectedFilter.filter_name);
    console.log("Filter criteria:", selectedFilter.filter_criteria);

    setSelectedFilterId(selectedFilter.id);
    setFilterLabel(selectedFilter.filter_name);

    const criteria = selectedFilter.filter_criteria || {};

    if (selectedFilter.filter_name === "Unassigned" || criteria.filter_name === "unassigned") {
      // Inject assigned_to_name null filter for Unassigned
      criteria.filterModel = {
        ...(criteria.filterModel || {}),
        assigned_to_name: {
          filterType: "text",
          type: "inArray",
          filter: JSON.stringify(["Unassigned"]),
        },
      };
    } else {
      // For all other filters, remove assigned_to_name if it's not part of this filter's saved model
      const filterModel = { ...(criteria.filterModel || {}) };
      if (!criteria.filterModel?.assigned_to_name) {
        delete filterModel.assigned_to_name;
      }
      criteria.filterModel = filterModel;
    }
    
    // Build the new filter and page state
    const newFilterAndPage = {
      filter: JSON.stringify(criteria.filterModel || {}),
      page: 1,
      sort: JSON.stringify(criteria.sortModel || []),
      pageSize: selectedFilter.page_size || pageSize,
      defaultFilter: criteria.filter_name || null,
    };

    console.log("New filterAndPage:", newFilterAndPage);
    console.log("Filter to apply:", JSON.parse(newFilterAndPage.filter));
    console.log("Sort to apply:", JSON.parse(newFilterAndPage.sort));

    // Update both states
    isFilterChanging.current = true;
    
    // First update the external grid filter (for UI)
    setExternalGridFilter({
      ...criteria,
      pageSize: selectedFilter.page_size || pageSize,
    });

    // reset column order if filter has no saved column layout
    if ((!criteria.columnOrder || criteria.columnOrder.length === 0) && gridRef.current) {
      const colApi = gridRef.current.getColumnApi();
      if (colApi) {
        colApi.resetColumnState(); // resets to original columnDefs order/widths
      }
    }

    // Then update filterAndPage (triggers data load)
    setFilterAndPage(newFilterAndPage);

    // Update last_user_filter on backend
    dispatch(loadTicketFilterSaveDataRequest({ filter_id: selectedFilter.id }));

    // Reload filter list after delay
    setTimeout(() => {
      dispatch(loadUserTicketFilterGetDataRequest(FILTER_TYPES.TICKETS_LIST));
      isFilterChanging.current = false;
      console.log("=== FILTER SELECTION END ===");
    }, 500);
  };

  // Auto-select newly saved filter in dropdown once list reloads
  useEffect(() => {
    if (!pendingFilterNameRef.current || !userFilterList.length) return;
    const match = userFilterList.find(f => f.filter_name === pendingFilterNameRef.current);
    if (match) {
      setSelectedFilterId(match.id);
      setFilterLabel(match.filter_name);
      pendingFilterNameRef.current = null;

      // ✅ Persist the correct label + ID to Redux so navigation restore is correct
      dispatch(addTableFilter(tableFilterKey, {
        ...tableFilters[tableFilterKey],
        _filterLabel: match.filter_name,
        _selectedFilterId: match.id,
      }));
    }
  }, [userFilterList]);

  // Handle save new filter
    const handleSaveFilterRequest = (payload) => {
      const updatedPayload = { ...payload, is_default: true };
      dispatch(loadUserTicketFilterAddDataRequest(updatedPayload));

      const savedFilterCriteria = payload.filter_criteria || {};
      const newFilterAndPage = {
        filter: JSON.stringify(savedFilterCriteria.filterModel || {}),
        page: 1,
        sort: JSON.stringify(savedFilterCriteria.sortModel || null),
        pageSize: payload.page_size || pageSize,
        defaultFilter: savedFilterCriteria.filter_name || null,
      };

      setFilterAndPage(newFilterAndPage);
      pendingFilterNameRef.current = payload.filter_name;

      setTimeout(() => {
        dispatch(loadUserTicketFilterGetDataRequest(FILTER_TYPES.TICKETS_LIST));
        // ✅ REMOVE setFilterLabel here — let the useEffect handle it with the real ID
      }, 300);
    };

  

  // Handle filter deletion
  const handleDeleteFilter = (filterId, e) => {
    e.stopPropagation();
    console.log("Deleting filter:", filterId);
    dispatch(loadUserTicketFilterDeleteDataRequest(filterId));
  };

  // Handle opening save filter modal
  const handleOpenSaveFilterModal = () => {
    setSaveFilterName("");
    setSaveFilterDescription("");
    setSaveFilterIsShared(false);
    setSaveFilterNameError("");
    setShowSaveFilterModal(true);
  };

  // Handle saving filter from modal
  const handleSaveFilterClick = () => {
    // Validate filter name
    if (!saveFilterName.trim()) {
      setSaveFilterNameError("Filter name is required.");
      return;
    }

    // Trigger save via grid ref
    if (gridRef.current) {
      gridRef.current.saveFilter({
        filterName: saveFilterName.trim(),
        filterDescription: saveFilterDescription.trim(),
        isDefault: false, // Always false as per your existing logic
        isShared: saveFilterIsShared,
      });
    }

    setShowSaveFilterModal(false);
  };

  // Callback when ServerSideGrid triggers save (receives filter data with criteria)
  const handleOnSaveFilter = useCallback((filterData) => {
    console.log("💾 Saving filter from ServerSideGrid:", filterData);

    // Use your existing save filter action
    handleSaveFilterRequest(filterData);
  }, []);

  // Store current filter and sort in refs to access in callbacks
  const currentFilterRef = useRef({});
  const currentSortRef = useRef([]);
  const gridRef = useRef(null);

  // Track selected rows count
  const [selectedRowCount, setSelectedRowCount] = useState(0);

  // Save filter modal state
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState("");
  const [saveFilterDescription, setSaveFilterDescription] = useState("");
  const [saveFilterIsShared, setSaveFilterIsShared] = useState(false);
  const [saveFilterNameError, setSaveFilterNameError] = useState("");

  // Handle row selection change
  const handleSelectionChanged = useCallback(() => {
    if (!gridRef.current) return;
    const selectedRows = gridRef.current.getSelectedRows();
    setSelectedRowCount(selectedRows ? selectedRows.length : 0);
    setTicketRowCount(selectedRows ? selectedRows.filter((r) => r.ticket_type === "Ticket").length : 0);
    setBulkUpdate({ status_id: "", assigned_to: "", assigned_to_admin_group: "" });
    setBulkMessage({ text: "", type: "" });
  }, []);

  //  Add bulk apply handler
   const handleBulkApply = useCallback(() => {
      if (!gridRef.current) return;
      const selectedRows = gridRef.current.getSelectedRows();
      if (!selectedRows?.length) return;

      const ticketRows = selectedRows.filter((r) => r.ticket_type === "Ticket");

      if (ticketRows.length === 0) {
        setBulkMessage({ text: "Bulk update only applies to Tickets. No tickets selected.", type: "error" });
        return;
      }

      const payload = { ticket_ids: ticketRows.map((r) => r.id) };
      if (bulkUpdate.status_id)               payload.status_id = Number(bulkUpdate.status_id);
      if (bulkUpdate.assigned_to_admin_group) payload.assigned_to_admin_group = Number(bulkUpdate.assigned_to_admin_group);
      if (bulkUpdate.assigned_to)             payload.assigned_to = Number(bulkUpdate.assigned_to);

      if (Object.keys(payload).length === 1) {
        setBulkMessage({ text: "Please select at least one field to update.", type: "error" });
        return;
      }

      const skipped = selectedRows.length - ticketRows.length;
      if (skipped > 0) {
        setBulkMessage({ text: `${skipped} Workflow(s) skipped. Updating ${ticketRows.length} Ticket(s).`, type: "warning" });
      } else {
        setBulkMessage({ text: "", type: "" });
      }

      dispatch(loadTicketBulkUpdateRequest(payload));
    }, [bulkUpdate, dispatch]);

  // Load group employees when admin group is picked in bulk bar
    const handleBulkAdminGroupChange = (groupId) => {
      setBulkUpdate((prev) => ({ ...prev, assigned_to_admin_group: groupId, assigned_to: "" }));
      if (groupId) {
        dispatch(loadGroupByIDJobTitlesGetDataRequest(groupId)); 
      }
    };
  // After bulk update success
  useEffect(() => {
    if (!bulkUpdateSuccess) return;

    // Deselect all rows and reset bulk bar
    gridRef.current?.getApi()?.deselectAll();
    setBulkUpdate({ status_id: "", assigned_to: "", assigned_to_admin_group: "" });
    setBulkMessage({ text: "", type: "" });

    // ← Add this to refresh grid rows
    if (gridRef.current) {
      const api = gridRef.current.getApi();
      if (api) {
        api.purgeServerSideCache();
      }
    }

    // Refresh the grid data
    const filter = JSON.parse(filterAndPage.filter);
    const sort = JSON.parse(filterAndPage.sort);

    dispatch(loadAdminTicketsRowCountRequest({
      filter,
      defaultFilter: filterAndPage.defaultFilter,
    }));

    dispatch(loadAdminTicketsQueryRequest({
      filter,
      page: filterAndPage.page,
      sort,
      pageSize: filterAndPage.pageSize,
      defaultFilter: filterAndPage.defaultFilter,
    }));

  }, [bulkUpdateSuccess]);  


  // Handle grid filter changes
  const handleGridFilterChanged = useCallback((filterModel) => {
    if (!isInitialized.current) return;

    console.log('💾 Saving filter state to Redux:', filterModel);
    currentFilterRef.current = filterModel;

    const filterState = {
      filter: JSON.stringify(filterModel),
      sort: JSON.stringify(currentSortRef.current),
      page: 1,
      pageSize: pageSize,
      defaultFilter: filterAndPage.defaultFilter,
      _filterLabel: filterLabel,
      _selectedFilterId: selectedFilterId,
    };

    dispatch(addTableFilter(tableFilterKey, filterState));
  }, [dispatch, tableFilterKey, filterLabel, selectedFilterId, filterAndPage.defaultFilter]);

  // Handle grid sort changes
  const handleGridSortChanged = useCallback((sortModel) => {
    if (!isInitialized.current) return;

    console.log('💾 Saving sort state to Redux:', sortModel);
    currentSortRef.current = sortModel;

    const filterState = {
      filter: JSON.stringify(currentFilterRef.current),
      sort: JSON.stringify(sortModel),
      page: 1,
      pageSize: pageSize,
      defaultFilter: filterAndPage.defaultFilter,
      _filterLabel: filterLabel,
      _selectedFilterId: selectedFilterId,
    };

    dispatch(addTableFilter(tableFilterKey, filterState));
  }, [dispatch, tableFilterKey, filterLabel, selectedFilterId, filterAndPage.defaultFilter]);

  // Smart export: exports selected rows if any, otherwise exports current page
  const handleExport = useCallback(() => {
    if (!gridRef.current) {
      console.warn("Grid ref not available");
      return;
    }

    const selectedRows = gridRef.current.getSelectedRows();

    // If rows are selected, export only selected rows
    if (selectedRows && selectedRows.length > 0) {
      // Create CSV content manually for selected rows
      const headers = columnDefs.map(col => col.headerName || col.field).filter(Boolean);
      const fields = columnDefs.map(col => col.field).filter(Boolean);

      let csvContent = headers.join(",") + "\n";

      selectedRows.forEach(row => {
        const rowData = fields.map(field => {
          let value = row[field] || "";
          // Escape quotes and wrap in quotes if contains comma
          if (typeof value === 'string') {
            value = value.replace(/"/g, '""');
            if (value.includes(',') || value.includes('\n')) {
              value = `"${value}"`;
            }
          }
          return value;
        });
        csvContent += rowData.join(",") + "\n";
      });

      // Download CSV
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `selected_tickets_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`✅ Exported ${selectedRows.length} selected tickets`);
    } else {
      // No selection, export current page using AG Grid's built-in export
      const params = {
        fileName: `tickets_${new Date().toISOString().split('T')[0]}.csv`,
        columnKeys: columnDefs.map(col => col.field).filter(Boolean),
      };

      gridRef.current.exportDataAsCsv(params);
      console.log(`✅ Exported current page`);
    }
  }, [columnDefs]);

  // Export all data to CSV using backend export endpoint
  const handleExportAll = useCallback(() => {
    console.log("Exporting all tickets...");

    // Get current filter model from grid
    const currentFilter = gridRef.current?.getApi()?.getFilterModel() || {};
    const filter = Object.keys(currentFilter).length > 0 ? currentFilter : JSON.parse(filterAndPage.filter || '{}');

    // Get current sort model from grid
    // Use getColumnApi() method which we exposed in ServerSideGrid
    const columnApi = gridRef.current?.getColumnApi();
    const currentSort = columnApi?.getColumnState()
      ?.filter(col => col.sort != null)
      ?.map(col => ({ colId: col.colId, sort: col.sort })) || [];
    const sort = currentSort.length > 0 ? currentSort : JSON.parse(filterAndPage.sort || 'null');

    // Get exportable column fields
    const fields = columnDefs.map(col => col.field).filter(Boolean);

    console.log('Export All - Filter:', filter);
    console.log('Export All - Sort:', sort);
    console.log('Export All - Default Filter:', filterAndPage.defaultFilter);

    // Call API with ids: "all" to export all filtered records
    instance
      .post(
        "tickets/ticket/AdminTickets/export",
        {
          ids: "all", // String "all" to export all records
          filter: JSON.stringify(filter),
          sort: JSON.stringify(sort),
          fields: fields,
          defaultFilter: filterAndPage.defaultFilter || null,
        },
        { responseType: "blob" }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${TICKETING}_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        console.log('✅ Export all completed');
      })
      .catch((error) => {
        console.error("❌ Export all failed:", error);
        alert("Failed to export data. Please try again.");
      });
  }, [filterAndPage, columnDefs]);

  // Row click handler
  const rowClick = useCallback(
    (row) => {
      const { id, ticket_type, assigned_admin_group_name, assigned_to_name } = row.data;

      if (ticket_type === "Ticket") {
        navigate(`/ticketing/${id}`);
      } else if (ticket_type === "Workflow") {
        navigate(`/workflow/${id}`, {
          state: {
            assignedAdminGroup: assigned_admin_group_name,
            assignedToName: assigned_to_name,
          },
        });
      }
    },
    [navigate]
  );

  // reset on unmount so guard works on every navigation
  useEffect(() => {
    return () => { isGridStabilized.current = false; };
  }, []);

  // Column visibility handler
  const handleColumnStateChanged = useCallback(() => {
    if (!isInitialized.current || !gridRef.current) return;
    if (!isGridStabilized.current) return;

    const colApi = gridRef.current.getColumnApi();
    if (!colApi) return;

    const colState = colApi.getColumnState();

    // Build columnVisibility map
    const columnVisibility = {};
    colState.forEach(col => {
      columnVisibility[col.colId] = !col.hide;
    });

    // Build columnOrder with widths
    const columnOrder = colState.map(col => ({
      colId: col.colId,
      width: col.width,
      pinned: col.pinned || null,
    }));

    console.log('📐 Column state changed, saving:', columnVisibility);

    // Save to Redux so it persists across navigation
    const filterState = {
      filter: JSON.stringify(currentFilterRef.current),
      sort: JSON.stringify(currentSortRef.current),
      page: 1,
      pageSize: pageSize,
      defaultFilter: filterAndPage.defaultFilter,
      _filterLabel: filterLabel,
      _selectedFilterId: selectedFilterId,
      columnVisibility,   // ← persist visibility
      columnOrder,        // ← persist order & widths
    };

    dispatch(addTableFilter(tableFilterKey, filterState));
  }, [dispatch, tableFilterKey, filterLabel, selectedFilterId, filterAndPage.defaultFilter]);


  const truncate = (text, max = 25) =>  text.length > max ? text.substring(0, max) + "..." : text;

  // Auto refresh every 5 minutes
   useEffect(() => {
    if (!isReady) return;

    const interval = setInterval(() => {
      console.log(" Auto refreshing tickets...", new Date().toLocaleTimeString());

      const filter = JSON.parse(filterAndPage.filter);
      const sort = JSON.parse(filterAndPage.sort);

      dispatch(loadAdminTicketsRowCountRequest({
        filter,
        defaultFilter: filterAndPage.defaultFilter,
      }));

      dispatch(loadAdminTicketsQueryRequest({
        filter,
        page: filterAndPage.page,
        sort,
        pageSize: filterAndPage.pageSize,
        defaultFilter: filterAndPage.defaultFilter,
      }));

      // AG Grid v27 refresh
      if (gridRef.current) {
        const api = gridRef.current.getApi();
        if (api) {
          api.purgeServerSideCache();
        }
      }

    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isReady, dispatch, filterAndPage]);

 // Reset Filter
  const handleClearAllFilters = useCallback(() => {
    if (gridRef.current) {
      const api = gridRef.current.getApi();
      if (api) {
        api.setFilterModel({});
        api.deselectAll();
        api.purgeServerSideCache();
      }
    }

    // Find the system default filter
    const systemDefault =
      userFilterList.find((f) => f.filter_scope === "SYSTEM") ||
      userFilterList.find((f) => f?.filter_criteria?.filter_name === "my_group_tickets") ||
      userFilterList[0];

    if (systemDefault) {
      const criteria = systemDefault.filter_criteria || {};

      setSelectedFilterId(systemDefault.id);
      setFilterLabel(systemDefault.filter_name);
      setExternalGridFilter({
        ...criteria,
        pageSize: systemDefault.page_size || pageSize,
      });
      setFilterAndPage({
        filter: JSON.stringify(criteria.filterModel || {}),
        page: 1,
        sort: JSON.stringify(criteria.sortModel || []),
        pageSize: systemDefault.page_size || pageSize,
        defaultFilter: criteria.filter_name || null,
      });
    }

    setSelectedRowCount(0);
    setBulkUpdate({ status_id: "", assigned_to: "", assigned_to_admin_group: "" });
    setBulkMessage({ text: "", type: "" });
  }, [userFilterList, pageSize]);

  

  return (
    <>
      <Row className="detailsPageHeader">
        <Col>
          <Breadcrumbs children={[{ href: "tickets/ticketing/", label: TICKETING }]} />
        </Col>

        <Col md="3">
          <div className="d-flex justify-content-end">
            <Dropdown>
              <Dropdown.Toggle
                style={{
                  width: "250px",
                  display: "inline-flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#fff",
                  color: "#000",
                  border: "1px solid #ced4da",
                  fontSize: "15px",
                }}
              >
                {truncate(filterLabel || "Select Filter")}
              </Dropdown.Toggle>

              <Dropdown.Menu
                style={{
                  width: "250px",
                  maxHeight: "300px",
                  overflowY: "auto",
                  backgroundColor: "#fff",
                  border: "1px solid #d1d1d1",
                  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  fontSize: "15px",
                }}
                className="custom-filter-menu custom-scrollbar"
              >
                {userFilterList.map((f) => (
                  <Dropdown.Item
                    key={f.id}
                    onClick={() => handleFilterSelection(f)}
                    active={selectedFilterId === f.id}
                    className="d-flex align-items-center justify-content-between px-3"
                    style={{ height: "40px", color: "#212529" }}
                    title={f.filter_name}
                  >
                    <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "150px",
                        }}
                      >
                        {f.filter_name}
                      </span>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      {f.last_user_filter && (
                        <i
                          className="fa fa-check"
                          style={{ fontSize: "12px", color: "#28a745" }}
                          title="Currently selected"
                        />
                      )}

                      {f.filter_scope !== "SYSTEM" && f.can_delete && (
                        <i
                          className="fa fa-trash"
                          style={{
                            cursor: "pointer",
                            color: "#dc3545",
                            fontSize: "13px",
                          }}
                          onClick={(e) => handleDeleteFilter(f.id, e)}
                        />
                      )}
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </Col>

        <Col xs="auto" className="gx-0 me-2">
          <DefaultButton
            buttonClass={"default-btn-color"}
            buttonTitle="Save Filter"
            actionBlock={handleOpenSaveFilterModal}
          />
        </Col>

        <Col xs="auto" className="gx-0 me-2">
          <DefaultButton
            buttonClass={"default-btn-color"}
            buttonTitle={selectedRowCount > 0 ? `Export ${selectedRowCount}` : "Export"}
            actionBlock={handleExport}
          />
        </Col>

        <Col xs="auto" className="gx-0 me-2">
          <DefaultButton
            buttonClass={"default-btn-color"}
            buttonTitle="Export All"
            actionBlock={handleExportAll}
          />
        </Col>

        <Col xs="auto" className="gx-0">
          <DefaultButton
            buttonClass={"default-btn-color"}
            buttonTitle={CREATENEW}
            actionBlock={() => navigate("/ticketcreation")}
          />
        </Col>        
      </Row>
      
    <Row>
      <Card className="table-card">
        <Card.Body>

          {/* Bulk bar inside the same card, above the grid */}
          {selectedRowCount > 0 && (
            <div
              style={{
                background: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                padding: "10px 16px",
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              {/* Count badge */}
              <span className="ticket-selected-badge" >
                {ticketRowCount} ticket{ticketRowCount !== 1 ? "s" : ""} selected
              </span>

              <span style={{ color: "#6c757d", fontSize: "13px" }}>Bulk Update:</span>

              {/* Status */}
              <select
                className="form-select form-select-sm"
                style={{ width: "150px" }}
                value={bulkUpdate.status_id}
                onChange={(e) => { setBulkUpdate((prev) => ({ ...prev, status_id: e.target.value })); setBulkMessage({ text: "", type: "" }); }}
              >
                <option value="">Status...</option>
                {statusList.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

              {/* Admin Group */}
              <select
                className="form-select form-select-sm"
                style={{ width: "180px" }}
                value={bulkUpdate.assigned_to_admin_group}
                onChange={(e) => { handleBulkAdminGroupChange(e.target.value); setBulkMessage({ text: "", type: "" }); }}
              >
                <option value="">Admin Group...</option>
                {adminGroup.map((g) => (
                  <option key={g.group_id ?? g.id} value={g.group_id ?? g.id}>
                    {g.group_name}
                  </option>
                ))}
              </select>

              {/* Assigned To */}
              <select
                className="form-select form-select-sm"
                style={{ width: "180px" }}
                value={bulkUpdate.assigned_to}
                disabled={!bulkUpdate.assigned_to_admin_group}
                onChange={(e) => { setBulkUpdate((prev) => ({ ...prev, assigned_to: e.target.value })); setBulkMessage({ text: "", type: "" }); }}
              >
                <option value="">Assigned To...</option>
                {(Array.isArray(group) ? group : []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.emp_name ?? u.name}, {u.employee_id}
                  </option>
                ))}
              </select>

              {/* Apply */}
              <button type="button" className="btn btn-sm default-btn-color" onClick={handleBulkApply}>
                Apply
              </button>

              {/* Clear */}
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  gridRef.current?.getApi()?.deselectAll();
                  setBulkUpdate({ status_id: "", assigned_to: "", assigned_to_admin_group: "" });
                  setBulkMessage({ text: "", type: "" });
                }}
              >
                Clear
              </button>

              {/* Inline message */}
              {bulkMessage.text && (
                <span
                  style={{
                    fontSize: "13px",
                    color: bulkMessage.type === "error" ? "#dc3545" : bulkMessage.type === "warning" ? "#856404" : "#0f5132",
                    background: bulkMessage.type === "error" ? "#f8d7da" : bulkMessage.type === "warning" ? "#fff3cd" : "#d1e7dd",
                    borderRadius: "6px",
                    padding: "3px 10px",
                  }}
                >
                  {bulkMessage.type === "error" ? "⚠ " : bulkMessage.type === "warning" ? "ℹ " : "✓ "}
                  {bulkMessage.text}
                </span>
              )}
            </div>
          )}

          {/* Hint text - only shows when no rows selected */}
            {selectedRowCount === 0 && (
              <div style={{ 
                fontSize: '12px', 
                color: '#6c757d', 
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',  // ← pushes clear to the right
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className='fas fa-info-circle' style={{ color: '#05a5f0' }}></i>
                  <span>Select Multiple tickets to perform bulk updates</span>
                </div>

                {/* Clear all filters — right side */}
                <span
                  onClick={handleClearAllFilters}
                  title="Clear all filters"
                  style={{
                    cursor: 'pointer',
                    color: '#dc3545',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    userSelect: 'none',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  <i className="fas fa-undo-alt" style={{ fontSize: '16px' }} />
                  Clear Filters
                </span>
              </div>
            )}
          

          {/* Grid */}
          <ServerSideGrid
            ref={gridRef}
            columnDefs={columnDefs}
            apiEndpoint="tickets/ticket/adminTickets/query"
            apiPayload={serverSideApiPayload}
            onRowClicked={rowClick}
            tableHeight="70vh"
            cacheBlockSize={80}
            showPagination={true}
            paginationPageSize={80}
            externalFilter={externalGridFilter}
            onFilterChanged={handleGridFilterChanged}
            onSortChanged={handleGridSortChanged}
            onSelectionChanged={handleSelectionChanged}
            onSaveFilter={handleOnSaveFilter}
            filterType={FILTER_TYPES.TICKETS_LIST}
            theme="alpine"
              onColumnStateChanged={handleColumnStateChanged}
              onGridStabilized={() => { isGridStabilized.current = true; }}
          />

        </Card.Body>
      </Card>
    </Row>

      
      {/* Save Filter Modal */}
      <Modal show={showSaveFilterModal} onHide={() => setShowSaveFilterModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save Current Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Filter Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter filter name"
                value={saveFilterName}
                onChange={(e) => {
                  setSaveFilterName(e.target.value);
                  setSaveFilterNameError("");
                }}
                isInvalid={!!saveFilterNameError}
              />
              <Form.Control.Feedback type="invalid">
                {saveFilterNameError}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter filter description (optional)"
                value={saveFilterDescription}
                onChange={(e) => setSaveFilterDescription(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Share with other users"
                checked={saveFilterIsShared}
                onChange={(e) => setSaveFilterIsShared(e.target.checked)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveFilterModal(false)}>
            Cancel
          </Button>
          <Button
            style={{ backgroundColor: '#0a7674', borderColor: '#0a7674' }}
            onClick={handleSaveFilterClick}
          >
            Save Filter
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Ticketing;